import { useCallback, useMemo } from 'react'

import { capitalize } from 'es-toolkit'
import type { CSSProperties } from 'styled-components'

import { useAsyncResource } from '@subaio/components'
import type { Api } from '@subaio/utils'

import type { StatuspagePage, StatuspageStatus } from '../types'
import { StatuspageImpact } from '../types'
import { Container } from './Container'
import { Indicator } from './Indicator'
import { Placeholder } from './Placeholder'

type Payload = {
	page: StatuspagePage
	status: StatuspageStatus
}

type Props = {
	api: Api

	style?: CSSProperties
	className?: string
}

/**
 * A widget to show a status derived from Statuspage, eg. https://status.subaio.com
 * @example
 * ```tsx
 * <SystemStatusWidget resource={resource} />
 * ```
 */

export const SystemStatusWidget = ({ api, style, className }: Props) => {
	const resource = useAsyncResource(
		useCallback(async () => {
			const status = await api.get<Payload>('/v2/status.json')
			return status
		}, [api]),
	)

	const indicator = useMemo(() => {
		if (resource.status === 'done') return resource.value.status.indicator
		if (resource.status === 'errored') return StatuspageImpact.Critical
		return undefined
	}, [resource])

	return (
		<Container
			href={resource.status === 'done' ? resource.value.page.url : api.baseUrl.replace('/api', '')}
			target='_blank'
			rel='noreferrer'
			style={style}
			className={className}
		>
			<Indicator indicator={indicator} />
			<Placeholder width='144px' isLoading={resource.status === 'loading'}>
				<span>
					{resource.status === 'done' ? capitalize(resource.value.status.description) : 'Failed to get status'}
				</span>
			</Placeholder>
		</Container>
	)
}
